import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { ShoppingCart, ArrowLeft, Package, Check } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Countdown } from '../components/PromoBanners';
import { useCart } from '../context/CartContext';
import { getPriceData } from '../utils/currency';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function OfferPage() {
    const { ref } = useParams();
    const navigate = useNavigate();
    const { addToCart, fetchCart, sessionId } = useCart();
    const [offer, setOffer] = useState(null);
    const [notFound, setNotFound] = useState(false);
    const [adding, setAdding] = useState(false);

    useEffect(() => {
        window.scrollTo(0, 0);
        axios.get(`${API}/promotions/offer/${ref}`).then(res => {
            setOffer(res.data);
            document.title = `${res.data.seo?.title || res.data.name} | Вкусът на Турция`;
            axios.post(`${API}/promotions/${res.data.promo_id}/track`, { event: 'view' }).catch(() => {});
        }).catch(() => setNotFound(true));
    }, [ref]);

    if (notFound) {
        return (
            <div className="min-h-[60vh] flex flex-col items-center justify-center px-4 text-center">
                <p className="text-2xl font-bold">Тази оферта вече не е активна</p>
                <p className="text-muted-foreground mt-2">Разгледайте другите ни актуални промоции</p>
                <Link to="/promotions"><Button className="mt-4" data-testid="back-to-promotions">Виж всички промоции</Button></Link>
            </div>
        );
    }
    if (!offer) return <div className="min-h-[60vh] flex items-center justify-center text-muted-foreground">Зареждане...</div>;

    const hb = offer.has_banner || {};
    const bannerUrl = hb.desktop ? `${API}/promotions/${offer.promo_id}/banner/desktop` : (hb.mobile ? `${API}/promotions/${offer.promo_id}/banner/mobile` : null);
    const mobileUrl = hb.mobile ? `${API}/promotions/${offer.promo_id}/banner/mobile` : null;
    const isCombo = offer.type === 'combo';

    const addCombo = async () => {
        setAdding(true);
        try {
            await axios.post(`${API}/promotions/${offer.promo_id}/add-combo`, {}, { params: { session_id: sessionId } });
            await fetchCart(true);
            toast.success('Цялото комбо е добавено в количката! 🎉');
            navigate('/cart');
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Грешка при добавяне на комбото');
        } finally { setAdding(false); }
    };

    const addSingle = async (p) => {
        const ok = await addToCart(p.product_id, 1);
        if (ok) {
            toast.success('Добавено в количката', { description: p.name });
            axios.post(`${API}/promotions/${offer.promo_id}/track`, { event: 'add_to_cart' }).catch(() => {});
        }
    };

    return (
        <div className="min-h-screen bg-amber-50/30 dark:bg-slate-900" data-testid="offer-page">
            <div className="max-w-5xl mx-auto px-4 py-6">
                <Link to="/promotions" className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground mb-4">
                    <ArrowLeft className="w-4 h-4" /> Всички промоции
                </Link>

                {bannerUrl && (
                    <picture>
                        {mobileUrl && <source media="(max-width: 640px)" srcSet={mobileUrl} />}
                        <img src={bannerUrl} alt={offer.banner?.alt || offer.name} className="w-full rounded-2xl shadow-lg max-h-96 object-cover" />
                    </picture>
                )}

                <div className="mt-6 flex flex-wrap items-center gap-3">
                    <Badge className="bg-red-600 text-white">{offer.badge}</Badge>
                    <Badge variant="outline">{offer.type_label}</Badge>
                    {offer.show_countdown && offer.end_at && <Countdown endAt={offer.end_at} />}
                </div>
                <h1 className="text-2xl sm:text-4xl font-bold mt-3" data-testid="offer-title">{offer.name}</h1>
                {offer.description && <p className="text-muted-foreground mt-2 whitespace-pre-line">{offer.description}</p>}
                {(offer.start_at || offer.end_at) && (
                    <p className="text-xs text-muted-foreground mt-2">
                        {offer.start_at && `От ${new Date(offer.start_at).toLocaleString('bg-BG', { day: 'numeric', month: 'long', hour: '2-digit', minute: '2-digit' })}`}
                        {offer.end_at && ` до ${new Date(offer.end_at).toLocaleString('bg-BG', { day: 'numeric', month: 'long', hour: '2-digit', minute: '2-digit' })}`}
                    </p>
                )}

                {isCombo ? (
                    <div className="mt-6 border rounded-2xl bg-white dark:bg-slate-800 p-5 shadow-sm">
                        <h2 className="font-bold text-lg flex items-center gap-2"><Package className="w-5 h-5 text-amber-600" /> Какво включва офертата</h2>
                        <div className="mt-4 space-y-3">
                            {offer.products.map(p => (
                                <Link to={`/products/${p.product_id}`} key={p.product_id} className="flex items-center gap-3 border rounded-lg p-3 hover:bg-amber-50 dark:hover:bg-slate-700 transition-colors" data-testid={`combo-item-${p.product_id}`}>
                                    {p.image_url && <img src={p.image_url.startsWith('/') ? `${API.replace('/api', '')}${p.image_url}` : p.image_url} alt={p.name} className="w-14 h-14 object-cover rounded" loading="lazy" />}
                                    <div className="flex-1 min-w-0">
                                        <p className="font-medium truncate">{p.name}</p>
                                        <p className="text-xs text-muted-foreground">{p.combo_qty} бр. × {getPriceData(p.base_price).eurFormatted} €</p>
                                    </div>
                                    {p.stock >= p.combo_qty ? <Check className="w-4 h-4 text-green-600" /> : <span className="text-xs text-red-600 font-medium">Изчерпан</span>}
                                </Link>
                            ))}
                        </div>
                        <div className="mt-5 border-t pt-4 space-y-1">
                            <p className="text-sm">Редовна обща цена: <span className="line-through text-muted-foreground">{getPriceData(offer.regular_total).eurFormatted} €</span></p>
                            {offer.saving > 0 && <p className="text-green-700 font-semibold">Спестявате: {getPriceData(offer.saving).eurFormatted} €</p>}
                        </div>
                        <Button className="w-full mt-4 h-12 text-base" disabled={!offer.combo_available || adding} onClick={addCombo} data-testid="add-combo-btn">
                            <ShoppingCart className="w-5 h-5 mr-2" />
                            {adding ? 'Добавя се...' : offer.combo_available ? 'Добави цялото комбо в количката' : 'Продукт от комбото е изчерпан'}
                        </Button>
                    </div>
                ) : (
                    <div className="mt-6">
                        <h2 className="font-bold text-lg mb-4">Продукти в промоцията</h2>
                        {offer.products.length === 0 ? <p className="text-muted-foreground text-sm">Скоро добавяме продуктите.</p> : (
                            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
                                {offer.products.map(p => (
                                    <div key={p.product_id} className="border rounded-xl bg-white dark:bg-slate-800 overflow-hidden shadow-sm flex flex-col" data-testid={`offer-product-${p.product_id}`}>
                                        <Link to={`/products/${p.product_id}`} className="block h-32 bg-amber-50 dark:bg-slate-700">
                                            {p.image_url && <img src={p.image_url.startsWith('/') ? `${API.replace('/api', '')}${p.image_url}` : p.image_url} alt={p.name} className="w-full h-full object-cover" loading="lazy" />}
                                        </Link>
                                        <div className="p-3 flex flex-col flex-1">
                                            <p className="text-sm font-medium line-clamp-2 flex-1">{p.name}</p>
                                            <div className="mt-2 flex items-baseline gap-2 flex-wrap">
                                                {p.promo_price ? (
                                                    <>
                                                        <span className="font-bold text-red-600">{getPriceData(p.promo_price).eurFormatted} €</span>
                                                        <span className="text-xs line-through text-muted-foreground">{getPriceData(p.base_price).eurFormatted} €</span>
                                                    </>
                                                ) : <span className="font-bold">{getPriceData(p.base_price).eurFormatted} €</span>}
                                            </div>
                                            <Button size="sm" className="mt-2" disabled={p.stock <= 0} onClick={() => addSingle(p)} data-testid={`offer-add-${p.product_id}`}>
                                                <ShoppingCart className="w-3 h-3 mr-1" /> {p.stock > 0 ? 'Добави' : 'Изчерпан'}
                                            </Button>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
}
